<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import conversationNotesAPI from 'dashboard/api/conversationNotes';

const props = defineProps({
  conversation: {
    type: Object,
    required: true,
  },
  kanbanCard: {
    type: Object,
    default: null,
  },
  pipelineId: {
    type: [String, Number],
    required: true,
  },
});

const emit = defineEmits(['update-conversation']);

const { t } = useI18n();
const store = useStore();

// State
const newNote = ref('');
const isSubmitting = ref(false);
const notes = ref([]);
const loading = ref(false);

// Rich text editor state
const showToolbar = ref(false);
const textareaRef = ref(null);

// Removed mock data - using API only

const formatDate = (dateString) => {
  if (!dateString) return 'agora';
  
  const date = new Date(dateString);
  const now = new Date();
  const diffInMinutes = Math.floor((now - date) / (1000 * 60));
  
  if (diffInMinutes < 1) return 'agora';
  if (diffInMinutes < 60) return `${diffInMinutes}m atrás`;
  
  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) return `${diffInHours}h atrás`;
  
  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 30) return `${diffInDays}d atrás`;
  
  return date.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

const addNote = async () => {
  if (!newNote.value.trim() || isSubmitting.value) return;
  
  isSubmitting.value = true;
  
  try {
    const response = await conversationNotesAPI.create(props.conversation.id, {
      content: newNote.value,
      pipeline_id: props.pipelineId,
    });
    
    notes.value.unshift(response.data);
    newNote.value = '';
    showToolbar.value = false;
    
    // Emit update event
    emit('update-conversation', { ...props.conversation });
    
  } catch (error) {
    console.error('Error adding note:', error);
    // TODO: Show error toast
  } finally {
    isSubmitting.value = false;
  }
};

const handleTextareaFocus = () => {
  showToolbar.value = true;
};

const handleTextareaBlur = () => {
  // Keep toolbar visible if there's content
  if (!newNote.value.trim()) {
    showToolbar.value = false;
  }
};

const insertFormatting = (type) => {
  const textarea = textareaRef.value;
  if (!textarea) return;
  
  const start = textarea.selectionStart;
  const end = textarea.selectionEnd;
  const selectedText = newNote.value.substring(start, end);
  
  let replacement = '';
  
  switch (type) {
    case 'bold':
      replacement = `**${selectedText || 'texto em negrito'}**`;
      break;
    case 'italic':
      replacement = `*${selectedText || 'texto em itálico'}*`;
      break;
    case 'underline':
      replacement = `__${selectedText || 'texto sublinhado'}__`;
      break;
    case 'strikethrough':
      replacement = `~~${selectedText || 'texto riscado'}~~`;
      break;
    case 'code':
      replacement = `\`${selectedText || 'código'}\``;
      break;
    case 'link':
      replacement = `[${selectedText || 'texto do link'}](url)`;
      break;
  }
  
  newNote.value = newNote.value.substring(0, start) + replacement + newNote.value.substring(end);
  
  // Set cursor position after formatting
  setTimeout(() => {
    if (selectedText) {
      textarea.setSelectionRange(start, start + replacement.length);
    } else {
      const cursorPos = start + replacement.indexOf(selectedText || replacement.match(/[a-zA-Z]/)?.[0] || '');
      textarea.setSelectionRange(cursorPos, cursorPos);
    }
    textarea.focus();
  }, 0);
};

const handleKeydown = (event) => {
  // Ctrl/Cmd + Enter to submit
  if ((event.ctrlKey || event.metaKey) && event.key === 'Enter') {
    event.preventDefault();
    addNote();
  }
  
  // Ctrl/Cmd + B for bold
  if ((event.ctrlKey || event.metaKey) && event.key === 'b') {
    event.preventDefault();
    insertFormatting('bold');
  }
  
  // Ctrl/Cmd + I for italic
  if ((event.ctrlKey || event.metaKey) && event.key === 'i') {
    event.preventDefault();
    insertFormatting('italic');
  }
};

const loadNotes = async () => {
  loading.value = true;
  try {
    // Se temos um kanbanCard, usar dados específicos do card
    // Senão, usar notas da conversa filtradas por pipeline
    if (props.kanbanCard) {
      // Para cards específicos, carregar notas da conversa filtradas por pipeline
      // As notas já são específicas por pipeline no backend
      const response = await conversationNotesAPI.get(props.conversation.id, {
        pipeline_id: props.pipelineId
      });
      notes.value = response.data;
    } else {
      // Fallback para conversas sem card específico
      const response = await conversationNotesAPI.get(props.conversation.id, {
        pipeline_id: props.pipelineId
      });
      notes.value = response.data;
    }
  } catch (error) {
    console.error('Error loading notes:', error);
    notes.value = [];
  } finally {
    loading.value = false;
  }
};

onMounted(() => {
  loadNotes();
});
</script>

<template>
  <div class="p-6">
    <!-- Add Note Form -->
    <div class="mb-6">
      <div class="relative">
        <textarea
          ref="textareaRef"
          v-model="newNote"
          placeholder="Teste de Notas 📝"
          class="w-full p-4 border border-n-weak rounded-lg resize-none transition-all duration-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 focus:outline-none"
          :class="{ 'min-h-[120px]': showToolbar, 'min-h-[60px]': !showToolbar }"
          @focus="handleTextareaFocus"
          @blur="handleTextareaBlur"
          @keydown="handleKeydown"
        />
        
        <!-- Character count -->
        <div class="absolute bottom-2 right-2 text-xs text-n-slate-11 pointer-events-none">
          {{ newNote.length }}/1000
        </div>
      </div>
      
      <!-- Toolbar -->
      <div
        v-if="showToolbar"
        class="flex items-center justify-between mt-3 p-3 bg-n-solid-1 rounded-lg border border-n-weak"
      >
        <div class="flex items-center gap-2">
          <!-- Formatting buttons -->
          <button
            @click="insertFormatting('bold')"
            class="p-2 text-n-slate-9 hover:text-n-slate-12 hover:bg-n-solid-2 rounded transition-colors"
            title="Negrito (Ctrl+B)"
          >
            <i class="i-lucide-bold size-4" />
          </button>
          
          <button
            @click="insertFormatting('italic')"
            class="p-2 text-n-slate-9 hover:text-n-slate-12 hover:bg-n-solid-2 rounded transition-colors"
            title="Itálico (Ctrl+I)"
          >
            <i class="i-lucide-italic size-4" />
          </button>
          
          <button
            @click="insertFormatting('underline')"
            class="p-2 text-n-slate-9 hover:text-n-slate-12 hover:bg-n-solid-2 rounded transition-colors"
            title="Sublinhado"
          >
            <i class="i-lucide-underline size-4" />
          </button>
          
          <button
            @click="insertFormatting('strikethrough')"
            class="p-2 text-n-slate-9 hover:text-n-slate-12 hover:bg-n-solid-2 rounded transition-colors"
            title="Riscado"
          >
            <i class="i-lucide-strikethrough size-4" />
          </button>
          
          <div class="w-px h-6 bg-n-weak mx-1" />
          
          <button
            @click="insertFormatting('code')"
            class="p-2 text-n-slate-9 hover:text-n-slate-12 hover:bg-n-solid-2 rounded transition-colors"
            title="Código"
          >
            <i class="i-lucide-code size-4" />
          </button>
          
          <button
            @click="insertFormatting('link')"
            class="p-2 text-n-slate-9 hover:text-n-slate-12 hover:bg-n-solid-2 rounded transition-colors"
            title="Link"
          >
            <i class="i-lucide-link size-4" />
          </button>
        </div>
        
        <div class="flex items-center gap-2">
          <button
            @click="showToolbar = false; newNote = ''"
            class="px-3 py-2 text-sm text-n-slate-11 hover:text-n-slate-12 transition-colors"
          >
            Cancelar
          </button>
          
          <button
            @click="addNote"
            :disabled="!newNote.trim() || isSubmitting"
            class="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:bg-n-slate-6 disabled:cursor-not-allowed rounded-lg transition-colors"
          >
            <span v-if="isSubmitting">Adicionando...</span>
            <span v-else>Adicionar</span>
          </button>
        </div>
      </div>
    </div>
    
    <!-- Notes List -->
    <div class="space-y-4">
      <div v-if="loading" class="flex items-center justify-center py-8">
        <div class="animate-spin rounded-full h-6 w-6 border-b-2 border-n-slate-9" />
      </div>
      
      <div v-else-if="!notes.length" class="text-center py-8">
        <div class="w-16 h-16 mx-auto mb-4 bg-n-solid-2 rounded-full flex items-center justify-center">
          <i class="i-lucide-sticky-note size-8 text-n-slate-9" />
        </div>
        <h3 class="text-lg font-medium text-n-slate-12 mb-2">Nenhuma nota ainda</h3>
        <p class="text-n-slate-11">Adicione sua primeira nota para começar a documentar informações importantes sobre este lead.</p>
      </div>
      
      <div
        v-for="note in notes"
        :key="note.id"
        class="bg-n-background border border-n-weak rounded-lg p-4 hover:shadow-sm transition-shadow"
      >
        <!-- Note Header -->
        <div class="flex items-center gap-3 mb-3">
          <div class="relative">
            <img
              v-if="note.user?.avatar_url || note.author?.avatar_url"
              :src="note.user?.avatar_url || note.author?.avatar_url"
              :alt="note.user?.name || note.author?.name"
              class="w-8 h-8 rounded-full object-cover"
            />
            <div
              v-else
              class="w-8 h-8 rounded-full bg-n-slate-9 flex items-center justify-center text-white text-sm font-medium"
            >
              {{ (note.user?.name || note.author?.name || '?').charAt(0).toUpperCase() }}
            </div>
          </div>
          
          <div class="flex-1">
            <div class="flex items-center gap-2">
                  <span class="font-medium text-n-slate-12">{{ note.user?.name || note.author?.name }}</span>
                  <span class="text-sm text-n-slate-11">{{ formatDate(note.created_at) }}</span>
            </div>
          </div>
          
          <!-- Note Actions -->
          <div class="flex items-center gap-1">
            <button class="p-1.5 text-n-slate-9 hover:text-n-slate-12 hover:bg-n-solid-2 rounded transition-colors">
              <i class="i-lucide-edit-3 size-4" />
            </button>
            <button class="p-1.5 text-n-slate-9 hover:text-red-600 hover:bg-red-50 rounded transition-colors">
              <i class="i-lucide-trash-2 size-4" />
            </button>
          </div>
        </div>
        
        <!-- Note Content -->
        <div class="prose prose-sm max-w-none">
          <p class="text-n-slate-12 whitespace-pre-wrap">{{ note.content }}</p>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Textarea auto-resize */
textarea {
  resize: none;
  overflow: hidden;
}

/* Prose styles for note content */
.prose p {
  margin: 0;
  line-height: 1.5;
}
</style>
